/**
 * VacationIndicator - the one visible trace of Vacation mode outside
 * /admin/autonomy.
 *
 * Vacation relaxes every surface ceiling to Full Auto, which changes what Neo
 * does on the operator's behalf everywhere - yet nothing in the shell said so.
 * This chip renders ONLY while Vacation is active and links to the autonomy
 * page where it can be turned off. Display-only: it reads the same GET
 * /api/autonomy the admin page uses (shared ['autonomy'] cache key, so the
 * page's own mutations refresh it instantly) and never touches gate logic.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Plane } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Tooltip } from '../ui/tooltip.js';

interface VacationSlice {
  vacation?: { active: boolean; until: string | null };
}

export default function VacationIndicator({ compact = false }: { compact?: boolean }) {
  const { data } = useQuery({
    queryKey: ['autonomy'],
    queryFn: () => api.get<VacationSlice>('/api/autonomy'),
    staleTime: 5 * 60_000,
    retry: false,
    // Silently absent when the read fails (e.g. role without autonomy access).
    throwOnError: false,
  });

  const vacation = data?.vacation;
  if (!vacation?.active) return null;

  const untilLabel = vacation.until
    ? new Date(vacation.until).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;

  const chip = (
    <Link
      href="/admin/autonomy"
      aria-label={`Vacation mode is on${untilLabel ? ` until ${untilLabel}` : ''}. Neo runs at Full Auto. Open Autonomy to review.`}
      className={[
        'inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning-bg font-semibold text-warning transition-colors hover:border-warning/70',
        compact ? 'h-10 w-10 justify-center rounded-xl shadow-sm ring-1 ring-warning/30 border-0' : 'whitespace-nowrap px-2 py-0.5 text-[11px]',
      ].join(' ')}
    >
      <Plane size={compact ? 16 : 11} aria-hidden />
      {!compact && <span>Vacation{untilLabel ? ` until ${untilLabel}` : ''}</span>}
    </Link>
  );

  return (
    <Tooltip content="Vacation is on: Neo runs at Full Auto on your surfaces (external sends stay gated). Click to review." side="bottom">
      {chip}
    </Tooltip>
  );
}
